import { useQuery } from "react-query";
import { z } from "zod";
import { publicApiCall } from "src/api/axiosClient/userApi";

const CustomThemeConfigSchema = z.object({
    hasCustomTheme: z.boolean(),
    config: z
        .object({
            name: z.string(),
            baseTheme: z.string(),
            isActive: z.boolean(),
            palette: z
                .object({
                    primary: z.record(z.string(), z.string()).optional(),
                    secondary: z.record(z.string(), z.string()).optional(),
                })
                .optional(),
            typography: z.record(z.string(), z.unknown()).optional(),
            spacing: z.record(z.string(), z.unknown()).optional(),
            components: z.record(z.string(), z.unknown()).optional(),
            custom: z
                .object({
                    logoUrl: z.string().optional(),
                    faviconUrl: z.string().optional(),
                })
                .optional(),
        })
        .nullable()
        .optional(),
});

export type CustomThemeConfig = z.infer<typeof CustomThemeConfigSchema>;

export const useCustomTheme = () => {
    const query = useQuery({
        queryKey: ["customTheme"],
        queryFn: async (): Promise<CustomThemeConfig> => {
            const response = await publicApiCall.get("/api/theme/active");
            return CustomThemeConfigSchema.parse(response.data);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    });

    return {
        customThemeConfig: query.data,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
    };
};
